import Box from '@mui/material/Box';
import { type KeyboardEvent, useId } from 'react';
import { fonts } from '@/shared/theme';

export interface ColumnItem {
  key: string;
  label: string;
  value: number;
  /** Text above the bar (formatted value). */
  display?: string;
  /** Dashed marker for a target/reference value. */
  target?: number | null;
  /** Tooltip text. */
  title?: string;
  onClick?: () => void;
}

interface ColumnChartProps {
  items: ColumnItem[];
  formatAxis: (value: number) => string;
  /** Optional line through all bars (e.g. rolling average), same scale. */
  line?: number[];
  height?: number;
  /** Drawing width in viewBox units; wider keeps text at its size in a large container. */
  width?: number;
  minWidth?: number;
  ariaLabel: string;
  /** Solid bar colour instead of the theme gradient (e.g. one colour per measure). */
  color?: string;
  /** Hide the value labels above the bars; the tooltip still shows each value. */
  hideValues?: boolean;
}

const PAD_L = 64;
const PAD_R = 20;
const TOP = 30;
/** Rough advance width of one character of the 12px axis label, in viewBox units. */
const LABEL_CHAR_W = 6.6;
/** Same for the 12.5px bold value label above a bar. */
const VALUE_CHAR_W = 7.6;
/** Gap between a bar's top and its value label. */
const VALUE_GAP = 8;
/** Angled labels are cut to this many characters; the full name stays in the tooltip. */
const ANGLED_MAX_CHARS = 16;
const ANGLE = 40;

function niceMax(value: number) {
  if (value <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(value));
  const n = value / exp;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * exp;
}

/** Click/keyboard handling for a bar; bars without `onClick` stay purely presentational. */
function barInteraction(item: ColumnItem) {
  const onClick = item.onClick;
  if (!onClick) return {};
  return {
    role: 'button' as const,
    tabIndex: 0,
    style: { cursor: 'pointer' },
    'aria-label': item.title ?? `${item.label}: ${item.display ?? item.value}`,
    onClick,
    onKeyDown: (e: KeyboardEvent<SVGGElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
  };
}

/** Column chart in the prototype's style: gradient bars on tracks, dashed grid, value labels. */
export function ColumnChart({
  items,
  formatAxis,
  line,
  height = 340,
  width: W = 760,
  minWidth = 600,
  ariaLabel,
  color,
  hideValues = false,
}: ColumnChartProps) {
  const gradientId = useId();
  const slot = (W - PAD_L - PAD_R) / Math.max(1, items.length);
  // Labels stay horizontal while every one fits its column; otherwise they are angled so long
  // location names don't run into each other when there are many columns.
  const angled = items.some((i) => i.label.length * LABEL_CHAR_W > slot - 8);
  const longest = Math.min(ANGLED_MAX_CHARS, Math.max(0, ...items.map((i) => i.label.length)));
  const labelDrop = angled
    ? Math.ceil(longest * LABEL_CHAR_W * Math.sin((ANGLE * Math.PI) / 180)) + 30
    : 62;
  // Value labels too wide for their column would run into their neighbours, so they turn
  // vertical; the plot then starts lower to leave room above the tallest bar.
  const valueW = hideValues
    ? 0
    : Math.max(0, ...items.map((i) => i.display?.length ?? 0)) * VALUE_CHAR_W;
  const verticalValues = valueW > slot - 4;
  const plotTop = verticalValues ? Math.max(TOP, valueW + VALUE_GAP + 4) : TOP;
  const svgH = height + (angled ? Math.max(0, labelDrop - 62) : 0) + (plotTop - TOP);
  const base = svgH - labelDrop;
  const plotH = base - plotTop;
  const max = niceMax(
    Math.max(...items.map((i) => Math.max(i.value, i.target ?? 0)), ...(line ?? []), 0),
  );
  const y = (v: number) => base - (v / max) * plotH;
  const barW = Math.min(64, slot * 0.56);
  const grid = [0.25, 0.5, 0.75, 1].map((f) => f * max);
  // Barely rounded, matching the rest of the UI's tight corners.
  const r = Math.min(2, barW / 2);

  const roundedTop = (x: number, top: number, w: number, bottom: number) => {
    const rr = Math.min(r, Math.max(0, bottom - top));
    return `M${x},${bottom} L${x},${top + rr} Q${x},${top} ${x + rr},${top} L${x + w - rr},${top} Q${x + w},${top} ${x + w},${top + rr} L${x + w},${bottom} Z`;
  };

  const linePath = line
    ?.map(
      (v, i) =>
        `${i === 0 ? 'M' : 'L'}${(PAD_L + slot * i + slot / 2).toFixed(1)},${y(v).toFixed(1)}`,
    )
    .join(' ');

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${svgH}`}
        role="img"
        aria-label={ariaLabel}
        sx={{ width: '100%', minWidth, height: 'auto', display: 'block' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color ?? 'var(--mui-palette-md-primaryBright)'} />
            <stop
              offset="100%"
              stopColor={color ?? 'var(--mui-palette-md-primary)'}
              stopOpacity={color ? 0.8 : 1}
            />
          </linearGradient>
        </defs>
        {grid.map((v) => (
          <g key={v}>
            <line
              x1={PAD_L}
              y1={y(v)}
              x2={W - PAD_R}
              y2={y(v)}
              stroke="var(--mui-palette-md-border)"
              strokeDasharray="2 6"
            />
            <text
              x={PAD_L - 8}
              y={y(v) + 4}
              textAnchor="end"
              fontSize={11}
              fill="var(--mui-palette-md-faint)"
              fontFamily={fonts.body}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatAxis(v)}
            </text>
          </g>
        ))}
        <line
          x1={PAD_L}
          y1={base}
          x2={W - PAD_R}
          y2={base}
          stroke="var(--mui-palette-md-borderStrong)"
          strokeWidth={1.4}
        />
        {items.map((item, i) => {
          const x = PAD_L + slot * i + (slot - barW) / 2;
          const top = y(Math.max(0, item.value));
          const cx = x + barW / 2;
          return (
            <g key={item.key} {...barInteraction(item)}>
              <title>{item.title ?? `${item.label}: ${item.display ?? item.value}`}</title>
              <path d={roundedTop(x, plotTop, barW, base)} fill="var(--mui-palette-md-panel2)" />
              <path d={roundedTop(x, top, barW, base)} fill={`url(#${gradientId})`} />
              {item.target != null && item.target > 0 && (
                <line
                  x1={x - 6}
                  x2={x + barW + 6}
                  y1={y(item.target)}
                  y2={y(item.target)}
                  stroke="var(--mui-palette-md-text)"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  opacity={0.6}
                />
              )}
              {item.display && !hideValues && (
                <text
                  x={cx}
                  y={verticalValues ? top - VALUE_GAP : Math.max(14, top - VALUE_GAP)}
                  textAnchor={verticalValues ? 'start' : 'middle'}
                  dominantBaseline={verticalValues ? 'middle' : undefined}
                  transform={verticalValues ? `rotate(-90 ${cx} ${top - VALUE_GAP})` : undefined}
                  fontSize={12.5}
                  fontWeight={800}
                  fill="var(--mui-palette-md-text)"
                  fontFamily={fonts.head}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {item.display}
                </text>
              )}
              {angled ? (
                <text
                  x={cx}
                  y={base + 18}
                  textAnchor="end"
                  transform={`rotate(-${ANGLE} ${cx} ${base + 18})`}
                  fontSize={12}
                  fill="var(--mui-palette-md-muted)"
                  fontFamily={fonts.body}
                  fontWeight={600}
                >
                  {item.label.length > ANGLED_MAX_CHARS
                    ? `${item.label.slice(0, ANGLED_MAX_CHARS - 1)}…`
                    : item.label}
                </text>
              ) : (
                <text
                  x={cx}
                  y={base + 34}
                  textAnchor="middle"
                  fontSize={12}
                  fill="var(--mui-palette-md-muted)"
                  fontFamily={fonts.body}
                  fontWeight={600}
                >
                  {item.label}
                </text>
              )}
            </g>
          );
        })}
        {linePath && (
          <path
            d={linePath}
            fill="none"
            stroke="var(--mui-palette-md-primary)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.85}
          />
        )}
      </Box>
    </Box>
  );
}
